import { useState, useEffect } from 'react';
import { locationsApi } from '@/lib/api/locations';
import { sortIdsApi } from '@/lib/api/sort-ids';
import { LocationDetailModal } from '../components/location-detail-modal';
import { BarcodeModal } from '@/features/manifests/components/barcode-modal';
import { useDialog } from '@/hooks/useDialog';
import { Dialog } from '@/components/ui/dialog';
import { useUndoHistory } from '@/contexts/undo-history-context';
import type { Location, SortId } from '@/types/inventory';

type Tab = 'locations' | 'sortid1' | 'sortid2';

export function LocationsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('locations');

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Locations</h1>

      <div className="border-b border-gray-700">
        <nav className="flex gap-0">
          {(['locations', 'sortid1', 'sortid2'] as Tab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              {tab === 'locations' ? 'Locations' : tab === 'sortid1' ? 'Sort ID 1' : 'Sort ID 2'}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'locations' && <LocationsTab />}
      {activeTab === 'sortid1' && <SortIdTab type={1} />}
      {activeTab === 'sortid2' && <SortIdTab type={2} />}
    </div>
  );
}

function LocationsTab() {
  const { dialog, closeDialog, showError } = useDialog();
  const { push } = useUndoHistory();
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [viewingLocation, setViewingLocation] = useState<Location | null>(null);
  const [barcodeValue, setBarcodeValue] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Location | null>(null);
  const [deleteStrategy, setDeleteStrategy] = useState<'needsHome' | 'reassign'>('needsHome');
  const [reassignLocationId, setReassignLocationId] = useState<number | ''>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  const [formData, setFormData] = useState({
    code: '',
    description: '',
    isActive: true,
  });

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      const data = await locationsApi.getAll();
      setLocations(data ?? []);
    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingLocation(null);
    setFormData({ code: '', description: '', isActive: true });
    setShowModal(true);
  };

  const handleEdit = (location: Location) => {
    setEditingLocation(location);
    setFormData({
      code: location.code,
      description: location.description,
      isActive: location.isActive,
    });
    setShowModal(true);
  };

  const handleBarcode = (location: Location) => {
    setBarcodeValue(location.code);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        await locationsApi.update(editingLocation.id, formData);
      } else {
        await locationsApi.create(formData);
      }
      setShowModal(false);
      loadLocations();
    } catch (error) {
      console.error('Error saving location:', error);
      showError('Error', 'Failed to save location');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === locations.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(locations.map((l) => l.id)));
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkUpdate = async (isActive: boolean) => {
    if (selectedIds.size === 0) return;
    setIsBulkUpdating(true);
    try {
      await locationsApi.bulkUpdateActive(Array.from(selectedIds), isActive);
      setSelectedIds(new Set());
      loadLocations();
    } catch (error) {
      console.error('Error bulk updating locations:', error);
      showError('Error', `Failed to ${isActive ? 'activate' : 'deactivate'} selected locations`);
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleDeleteClick = (location: Location) => {
    setDeleteTarget(location);
    setDeleteStrategy('needsHome');
    setReassignLocationId('');
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    if (deleteStrategy === 'reassign' && !reassignLocationId) return;

    setIsDeleting(true);
    try {
      await locationsApi.delete(
        deleteTarget.id,
        deleteStrategy,
        deleteStrategy === 'reassign' ? Number(reassignLocationId) : undefined
      );
      push(
        deleteTarget.code ?? `Location #${deleteTarget.id}`,
        () => locationsApi.restore(deleteTarget.id),
        deleteTarget.description || 'Location deleted',
        loadLocations
      );
      setDeleteTarget(null);
      loadLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      showError('Error', 'Failed to delete location');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-300">
                {selectedIds.size} selected
              </span>
              <button
                onClick={() => handleBulkUpdate(true)}
                disabled={isBulkUpdating}
                className="px-3 py-1.5 text-sm bg-green-700 hover:bg-green-600 disabled:bg-green-900 disabled:cursor-not-allowed text-white rounded transition-colors"
              >
                {isBulkUpdating ? 'Updating...' : 'Activate'}
              </button>
              <button
                onClick={() => handleBulkUpdate(false)}
                disabled={isBulkUpdating}
                className="px-3 py-1.5 text-sm bg-yellow-700 hover:bg-yellow-600 disabled:bg-yellow-900 disabled:cursor-not-allowed text-white rounded transition-colors"
              >
                {isBulkUpdating ? 'Updating...' : 'Deactivate'}
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors"
              >
                Clear
              </button>
            </div>
          )}
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
        >
          Create Location
        </button>
      </div>

      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-3 py-3 w-10">
                <input
                  type="checkbox"
                  checked={locations.length > 0 && selectedIds.size === locations.length}
                  onChange={toggleSelectAll}
                  className="rounded"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Code</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Items</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {locations.map((location) => (
              <tr key={location.id} className={`hover:bg-gray-750 ${selectedIds.has(location.id) ? 'bg-gray-750' : ''}`}>
                <td className="px-3 py-4 w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(location.id)}
                    onChange={() => toggleSelect(location.id)}
                    className="rounded"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleBarcode(location)}
                      className="p-1 text-gray-400 hover:text-white transition-colors"
                      title="View barcode"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                      </svg>
                    </button>
                    <span className="font-mono text-blue-400">{location.code}</span>
                  </div>
                </td>
                <td className="px-6 py-4">{location.description}</td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => setViewingLocation(location)}
                    className="text-blue-400 hover:text-blue-300 hover:underline"
                  >
                    {location.itemCount}
                  </button>
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`px-2 py-1 text-xs rounded ${
                      location.isActive ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'
                    }`}
                  >
                    {location.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 space-x-2">
                  <button onClick={() => handleEdit(location)} className="text-blue-400 hover:text-blue-300">Edit</button>
                  <button onClick={() => handleDeleteClick(location)} className="text-red-400 hover:text-red-300">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {locations.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            No locations found. Create your first location to get started.
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingLocation ? 'Edit Location' : 'Create Location'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Code</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                  required
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                  rows={3}
                  required
                  maxLength={500}
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="locationIsActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="locationIsActive" className="text-sm">Active</label>
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                >
                  {editingLocation ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-2">Delete Location</h2>
            <p className="text-gray-400 mb-1">
              Delete <span className="font-mono text-blue-400">{deleteTarget.code}</span>?
            </p>
            {deleteTarget.itemCount > 0 && (
              <p className="text-yellow-400 text-sm mb-4">
                This location has {deleteTarget.itemCount} active item{deleteTarget.itemCount !== 1 ? 's' : ''}. Choose what to do with them:
              </p>
            )}
            {deleteTarget.itemCount === 0 && (
              <p className="text-gray-400 text-sm mb-4">
                This location has no active items.
              </p>
            )}

            <div className="space-y-3 mb-6">
              <label className="flex items-start gap-3 p-3 rounded border border-gray-600 cursor-pointer hover:border-gray-500 transition-colors">
                <input
                  type="radio"
                  name="deleteStrategy"
                  checked={deleteStrategy === 'needsHome'}
                  onChange={() => setDeleteStrategy('needsHome')}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium">Move items to &ldquo;Needs a Home&rdquo;</div>
                  <div className="text-sm text-gray-400">Items will have their location cleared and appear in the Needs a Home queue</div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 rounded border border-gray-600 cursor-pointer hover:border-gray-500 transition-colors">
                <input
                  type="radio"
                  name="deleteStrategy"
                  checked={deleteStrategy === 'reassign'}
                  onChange={() => setDeleteStrategy('reassign')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium">Reassign items to another location</div>
                  <div className="text-sm text-gray-400 mb-2">Items will be moved to the selected location</div>
                  {deleteStrategy === 'reassign' && (
                    <select
                      value={reassignLocationId}
                      onChange={(e) => setReassignLocationId(e.target.value ? Number(e.target.value) : '')}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500 text-sm"
                    >
                      <option value="">Select a location...</option>
                      {locations
                        .filter((l) => l.id !== deleteTarget.id && l.isActive)
                        .map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.code} — {l.description}
                          </option>
                        ))}
                    </select>
                  )}
                </div>
              </label>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={isDeleting || (deleteStrategy === 'reassign' && !reassignLocationId)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed rounded transition-colors"
              >
                {isDeleting ? 'Deleting...' : 'Delete Location'}
              </button>
            </div>
          </div>
        </div>
      )}

      <LocationDetailModal location={viewingLocation} onClose={() => setViewingLocation(null)} />

      <BarcodeModal value={barcodeValue} onClose={() => setBarcodeValue(null)} />

      <Dialog {...dialog} onClose={closeDialog} />
    </div>
  );
}

interface SortIdTabProps {
  type: 1 | 2;
}

function SortIdTab({ type }: SortIdTabProps) {
  const { dialog, closeDialog, showError, showConfirm } = useDialog();
  const { push } = useUndoHistory();
  const [sortIds, setSortIds] = useState<SortId[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<SortId | null>(null);
  const [barcodeValue, setBarcodeValue] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  const [formData, setFormData] = useState({
    code: '',
    description: '',
    isActive: true,
  });

  useEffect(() => {
    loadSortIds();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const loadSortIds = async () => {
    setIsLoading(true);
    try {
      const data = await sortIdsApi.getAll(type);
      setSortIds(data ?? []);
    } catch (error) {
      console.error('Error loading sort IDs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sortIds.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortIds.map((s) => s.id)));
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleBulkUpdate = async (isActive: boolean) => {
    if (selectedIds.size === 0) return;
    setIsBulkUpdating(true);
    try {
      await sortIdsApi.bulkUpdateActive(Array.from(selectedIds), isActive);
      setSelectedIds(new Set());
      loadSortIds();
    } catch (error) {
      console.error('Error bulk updating sort IDs:', error);
      showError('Error', `Failed to ${isActive ? 'activate' : 'deactivate'} selected Sort IDs`);
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({ code: '', description: '', isActive: true });
    setShowModal(true);
  };

  const handleEdit = (item: SortId) => {
    setEditingItem(item);
    setFormData({ code: item.code, description: item.description, isActive: item.isActive });
    setShowModal(true);
  };

  const handleBarcode = (item: SortId) => {
    setBarcodeValue(item.code);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await sortIdsApi.update(editingItem.id, formData);
      } else {
        await sortIdsApi.create({ ...formData, type });
      }
      setShowModal(false);
      loadSortIds();
    } catch (error) {
      console.error('Error saving sort ID:', error);
      showError('Error', 'Failed to save Sort ID');
    }
  };

  const handleDelete = async (id: number) => {
    const item = sortIds.find((s) => s.id === id);
    showConfirm(
      `Delete Sort ID ${type}`,
      'Delete this Sort ID? You can undo this from the history menu.',
      async () => {
        try {
          await sortIdsApi.delete(id);
          push(
            item?.code ?? `Sort ID ${type} #${id}`,
            () => sortIdsApi.restore(id),
            `Sort ID ${type} deleted`,
            loadSortIds
          );
          loadSortIds();
        } catch (error) {
          console.error('Error deleting sort ID:', error);
          showError('Error', 'Failed to delete Sort ID');
        }
      },
      'Delete'
    );
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-300">
                {selectedIds.size} selected
              </span>
              <button
                onClick={() => handleBulkUpdate(true)}
                disabled={isBulkUpdating}
                className="px-3 py-1.5 text-sm bg-green-700 hover:bg-green-600 disabled:bg-green-900 disabled:cursor-not-allowed text-white rounded transition-colors"
              >
                {isBulkUpdating ? 'Updating...' : 'Activate'}
              </button>
              <button
                onClick={() => handleBulkUpdate(false)}
                disabled={isBulkUpdating}
                className="px-3 py-1.5 text-sm bg-yellow-700 hover:bg-yellow-600 disabled:bg-yellow-900 disabled:cursor-not-allowed text-white rounded transition-colors"
              >
                {isBulkUpdating ? 'Updating...' : 'Deactivate'}
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors"
              >
                Clear
              </button>
            </div>
          )}
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
        >
          Create Sort ID {type}
        </button>
      </div>

      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-3 py-3 w-10">
                <input
                  type="checkbox"
                  checked={sortIds.length > 0 && selectedIds.size === sortIds.length}
                  onChange={toggleSelectAll}
                  className="rounded"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Code</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {sortIds.map((item) => (
              <tr key={item.id} className={`hover:bg-gray-750 ${selectedIds.has(item.id) ? 'bg-gray-750' : ''}`}>
                <td className="px-3 py-4 w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(item.id)}
                    onChange={() => toggleSelect(item.id)}
                    className="rounded"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleBarcode(item)}
                      className="p-1 text-gray-400 hover:text-white transition-colors"
                      title="View barcode"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                      </svg>
                    </button>
                    <span className="font-mono text-blue-400">{item.code}</span>
                  </div>
                </td>
                <td className="px-6 py-4">{item.description}</td>
                <td className="px-6 py-4">
                  <span
                    className={`px-2 py-1 text-xs rounded ${
                      item.isActive ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'
                    }`}
                  >
                    {item.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 space-x-2">
                  <button onClick={() => handleEdit(item)} className="text-blue-400 hover:text-blue-300">Edit</button>
                  <button onClick={() => handleDelete(item.id)} className="text-red-400 hover:text-red-300">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {sortIds.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            No Sort ID {type} entries found. Create your first entry to get started.
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingItem ? `Edit Sort ID ${type}` : `Create Sort ID ${type}`}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Code</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                  required
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                  rows={3}
                  required
                  maxLength={500}
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id={`sortIdIsActive-${type}`}
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor={`sortIdIsActive-${type}`} className="text-sm">Active</label>
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                >
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <BarcodeModal value={barcodeValue} onClose={() => setBarcodeValue(null)} />

      <Dialog {...dialog} onClose={closeDialog} />
    </div>
  );
}
